import React, { useCallback, useState, useEffect } from 'react';
import { Upload, message, Icon, Select } from 'antd';

import { unzipSync, strFromU8 } from 'fflate';

import { BulkUploadButton } from '../TemplateSection/styles';
import { uploadDKVDesignXML, uploadImage } from '../../utils/services/uploadAPI';
import { createStandaloneBlock, fetchStandaloneBlock, translateStandaloneBlock, patchComplexBlock } from '../../utils/services/complexDocumentsAPI';
import { allMatchKey } from '../../utils/importHelper';
import { IMPORT_REUSABLE_PREFIX, constructDesignFromInstructions } from './complexDocumentHelper';
import { extractUnsupportedCharacters } from '../../utils/helper';
import { useTranslation } from 'react-i18next';
import { DKV_CREATE_NEW_BLOCK } from './translationValues';
import { getAllCategories } from '../../utils/services/categoriesAPI';
import { getAllTeams } from '../../utils/services/teamsAPI';

const { Option } = Select;

const getBlockPayloadFromPseudo = (zipFile, fileName, languageCode, imageMap = {}, block = true, t, categoryId, resourceGroupId) => {
  console.log("Forming payload from :: ", fileName);
  const pseudoFileContents = strFromU8(zipFile[fileName]);
  if (pseudoFileContents.length === 0) {
    return;
  }
  let instructions = pseudoFileContents.split("\n").map((line, index) => {
    const tabs = line.includes('\t') ? line.split('\t').length - 1 : line.replaceAll('    ', '\t').split('\t').length - 1;
     return {
       tabs,
       instruction: line.replaceAll('\t', '').replaceAll('    ', '')
     };
   });
  console.log({ instructions });

  const designJSON = constructDesignFromInstructions(instructions, zipFile, true, languageCode, {}, imageMap, t);
  console.log({ documentDTO: designJSON });

  if (designJSON.flows[0] !== undefined && designJSON.flows[0].blocks.length > 0) {
    const blockName = designJSON.flows[0].flowName;
    if (block) {
      const payload = {
        categoryId: categoryId,
        resourceGroupId: resourceGroupId,
        parentBlockName: `${IMPORT_REUSABLE_PREFIX} ${designJSON.flows[0].flowName}_en`,
        isReferenced: true,
        blocks: designJSON.flows[0].blocks.map(block => {
          delete block.blockId;
          return block;
        }),
        type: blockName.toLowerCase().startsWith('footer') ? 'footer' : 'content',
        language: designJSON.language,
        style: designJSON.style || {
          fontFamily: "Arial",
          fontSize: 13
        }
      };
      return payload;
    } else {
      const updatedDocument = {
        flows: designJSON.flows.map((flow, index) => {
          delete flow.flowId;
          return Object.assign({}, flow, {
            blocks: flow.blocks.map(block => {
              if (!block.isStandalone) {
                delete block.blockId; // Delete blockId only if it is not a standalone
              }
              return block;
            })
          });
        })
      };
      return updatedDocument;
    }
  }
  return null;
}

const NAMESPACES = ['DKV_REUSABLE_IMPORT'];

const DKVReusableImport = () => {

  const { t } = useTranslation(NAMESPACES);

  const [loading, setLoading] = useState(false);
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [fetchingCategories, setFetchingCategories] = useState(true);

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await getAllTeams();
        if (response?.data) {
          setTeams(response.data);
          // Auto-select if only one team
          if (response.data.length === 1) {
            setSelectedTeamId(response.data[0].rgId);
          }
        }
      } catch (error) {
        console.error('Error fetching teams:', error);
        message.error('Failed to load teams');
      }
    };

    const fetchCategories = async () => {
      try {
        setFetchingCategories(true);
        const response = await getAllCategories(false, 'element');
        if (response?.statusCode === 507 && response?.data) {
          setCategories(response.data);
          // Auto-select if only one category
          if (response.data.length === 1) {
            setSelectedCategoryId(response.data[0].id);
          }
        } else {
          message.error('Failed to load categories');
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        message.error('Failed to load categories');
      } finally {
        setFetchingCategories(false);
      }
    };

    fetchTeams();
    fetchCategories();
  }, []);

  const onUploadXML = useCallback(async (event) => {
    if (!selectedTeamId) {
      message.error('Please select a team before uploading');
      return;
    }

    if (!selectedCategoryId) {
      message.error('Please select a category before uploading');
      return;
    }

    setLoading(true);

    // TODO : API to generate .zip file
    /*const response = {
      status: 200
    }*/
    console.log("Event :: ", event.name, event);
    const response = await uploadDKVDesignXML(event, event.name, false, 'sro');
    console.log({ response });

    if (response.status === 200) {
      // TODO : Extract zip file contents and obtain pseudo
      const arrayBuffer = await response.data.arrayBuffer();
      const zipFile = unzipSync(new Uint8Array(arrayBuffer));

      /*const zipFile = unzipSync(new Uint8Array(event.target.result), {
        filter(file) {
          // console.log({ file });
          return true;
        }
      });*/
      const pseudoFiles = allMatchKey(zipFile, '.txt', true);
      const imageFiles = allMatchKey(zipFile, '.txt', false);
      console.log({ zipFile, pseudoFiles, imageFiles });
      let uploaded = [];
      if (imageFiles.length > 0) {
        uploaded = await Promise.all(imageFiles.map((fileName) => {
          const file = new Blob([zipFile[fileName]], { type: 'image/jpeg' });
          return (async () => {
            let response = await uploadImage(file, `${IMPORT_REUSABLE_PREFIX} ${fileName.split('.')[0]}`);
            return {
              name: `${IMPORT_REUSABLE_PREFIX} ${fileName.split('.')[0]}`,
              link: response.imageLink
            };
          })();
        }));
      }
      const imageMap = uploaded.reduce((acc, image) => {
        return { ...acc,  [image.name]: image.link }
      }, []);
      console.log({ uploaded, imageMap });
      if (pseudoFiles.length > 0) {
        const pseudoFileName = 'en.txt';
        const payload = getBlockPayloadFromPseudo(zipFile, pseudoFileName, 'en', imageMap, true, t, selectedCategoryId, selectedTeamId);
        if (!payload) return;
        const createResponse = await createStandaloneBlock(payload);
        console.log({ createResponse });

        if (createResponse?.statusCode !== 2501) {
          const error = createResponse?.errors?.[0];

          if (error?.code === 5601) {
            const unsupportedChars = extractUnsupportedCharacters(error.description);

            message.error(
              t(
                DKV_CREATE_NEW_BLOCK.SECTION_NAME_UNSUPPORTED_CHARS.key,
                DKV_CREATE_NEW_BLOCK.SECTION_NAME_UNSUPPORTED_CHARS.value,
                { char: unsupportedChars }
              )
            );
          } else {
            message.error(
              t(
                DKV_CREATE_NEW_BLOCK.SECTION_CREATION_ERROR_MESSAGE.key,
                DKV_CREATE_NEW_BLOCK.SECTION_CREATION_ERROR_MESSAGE.value,
              ),
            );
          }

          return;
        }

        // TODO : If there are any other languages, create the section with the same group ID
        if (pseudoFiles.length > 1 && createResponse?.statusCode === 2501) { // success
          const blockId = createResponse.data?.id;
          const pendingFiles = pseudoFiles.filter(name => name !== 'en.txt');
          console.log({ pendingFiles });

          // TODO : Recursive method impl
          pendingFiles.forEach(async (targetLangPseudo) => {
            const lang = targetLangPseudo.split('.txt')[0];
            const response = await translateStandaloneBlock({ documentId: blockId, languageCode: lang });

            if (response.data.id) {
              const patchPayload = getBlockPayloadFromPseudo(zipFile, targetLangPseudo, lang, imageMap, false, t);
              if (patchPayload && patchPayload.flows[0].blocks.length > 0) {
                patchPayload.documentId = response.data?.id;
                patchPayload.language = lang;
                patchPayload.type = payload.type;
                if (patchPayload) {
                  const patchResponse = await patchComplexBlock(patchPayload, false);
                  console.log({ patchResponse });
                }
              }
            }
          });
        }
      } else {
        message.error("No contents found for importing!!");
      }
    }

    if (response?.status !== 200) {
      message.error("Unable to generate design script!!");
    }

    setLoading(false);
  }, [selectedTeamId, selectedCategoryId, t]);

  return (
    <div style={{ width: '95%', marginLeft: '5%', padding: '20px', minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
          Select Team: <span style={{ color: 'red' }}>*</span>
        </label>
        <Select
          style={{ width: '100%', maxWidth: '400px' }}
          placeholder="Select a team"
          value={selectedTeamId}
          onChange={setSelectedTeamId}
          disabled={loading}
        >
          {teams.map((team) => (
            <Option key={team.rgId || team.id} value={team.rgId || team.id}>
              {team.groupName || team.name || `Team ${team.rgId || team.id}`}
            </Option>
          ))}
        </Select>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
          Select Category: <span style={{ color: 'red' }}>*</span>
        </label>
        <Select
          style={{ width: '100%', maxWidth: '400px' }}
          placeholder="Select a category"
          value={selectedCategoryId}
          onChange={setSelectedCategoryId}
          loading={fetchingCategories}
          disabled={fetchingCategories || loading}
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {categories.map((category) => (
            <Option key={category.id} value={category.id}>
              {category.name}
            </Option>
          ))}
        </Select>
      </div>

      <Upload
        multiple={true}
        accept=".sro"
        // accept=".xml"
        showUploadList={false}
        className="dkvUpload"
        // action={importTemplate}
        action={onUploadXML}
        disabled={!selectedTeamId || !selectedCategoryId || loading}
      >
        <BulkUploadButton style={{ margin: 'auto', width: '50%', opacity: (!selectedTeamId || !selectedCategoryId) ? 0.5 : 1 }}>
          <p className={'downloadZip'} style={{ visibility: loading ? 'visible' : 'hidden' }}>
            <Icon
              type={'loading'}
              style={{ marginRight: '5px', fontSize: '20px', color: '#FC6E51' }}
            />
          </p>
          <p className="import">
            {'Upload your SRO files'}
          </p>
          <p className="desc">
            {'We create the reusable sections for you so you can use them within DocStudio'}
          </p>
        </BulkUploadButton>
      </Upload>
    </div>
  );
}

export default DKVReusableImport;